import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams, Navigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import InvestorPortalDetailHeader from "../../../../components/Admin/InvestorPortal/InvestorPortalDetailHeader";
import OpportunityEditor from "../../../../components/Admin/InvestorPortal/OpportunityEditor";
import TableSkeletonLoader from "../../../../components/Common/TableSkeletonLoader";
import {
  useCreateStaffOpportunityMutation,
  useGetStaffOpportunityQuery,
  useStaffOpportunityLifecycleMutation,
  useUpdateStaffOpportunityMutation,
} from "../../../../store/admin/investorPortal/staffInvestmentsApi";
import { mapStaffOpportunityDetail, mapStaffOpportunityFormToApi } from "../../../../utils/staffInvestmentsAdapter";
import { isOpportunityEditable } from "../../../../store/admin/investorPortal/opportunityLifecycle";
import { withInvestorPortalGuard } from "../withInvestorPortalGuard";

function OpportunityFormPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { opportunityId } = useParams();
  const isEdit = Boolean(opportunityId);
  const resumeStep = location.state?.editorStep;

  const { data: apiExisting, isFetching, isError } = useGetStaffOpportunityQuery(opportunityId, {
    skip: !isEdit,
  });
  const [createOpportunityApi] = useCreateStaffOpportunityMutation();
  const [updateOpportunityApi] = useUpdateStaffOpportunityMutation();
  const [lifecycleAction] = useStaffOpportunityLifecycleMutation();
  const [localOpportunity, setLocalOpportunity] = useState(null);

  const existing = useMemo(() => {
    if (localOpportunity) return localOpportunity;
    return mapStaffOpportunityDetail(apiExisting);
  }, [apiExisting, localOpportunity]);

  const editBlocked =
    isEdit && existing && !isOpportunityEditable(existing.status);

  useEffect(() => {
    if (!editBlocked || !opportunityId) return;
    toast.error(
      "This opportunity can’t be edited in its current status. Use Reopen for investment first."
    );
    navigate(`/super_admin/investor_portal/investments/opportunities/${opportunityId}`, {
      replace: true,
    });
  }, [editBlocked, opportunityId, navigate]);

  if (isEdit && isFetching && !localOpportunity) {
    return (
      <div className="pb-4">
        <TableSkeletonLoader rows={8} columns={2} />
      </div>
    );
  }

  if (isEdit && !localOpportunity && (!existing || isError)) {
    return <Navigate to="/super_admin/investor_portal/investments/opportunities" replace />;
  }

  if (editBlocked) {
    return (
      <div className="pb-4">
        <TableSkeletonLoader rows={8} columns={2} />
      </div>
    );
  }

  const handleSave = async (payload, submitAction = "draft", meta = {}) => {
    const body = mapStaffOpportunityFormToApi(payload);
    const id = opportunityId || payload?.id || localOpportunity?.id;

    try {
      let savedRaw;
      if (id) {
        savedRaw = await updateOpportunityApi({ id, body }).unwrap();
      } else {
        savedRaw = await createOpportunityApi(body).unwrap();
      }

      const mapped = mapStaffOpportunityDetail(savedRaw);
      const savedId = mapped?.id || savedRaw?.id;
      setLocalOpportunity(mapped);

      if (!isEdit && savedId && !opportunityId) {
        navigate(`/super_admin/investor_portal/investments/opportunities/${savedId}/edit`, {
          replace: true,
          state: { editorStep: meta.nextStep ?? resumeStep ?? 1 },
        });
      }

      if (submitAction === "review" || submitAction === "publish") {
        const action = submitAction === "review" ? "submit-review" : "publish";
        const afterLifecycle = await lifecycleAction({ id: savedId, action }).unwrap();
        const lifecycleMapped = mapStaffOpportunityDetail(afterLifecycle);
        navigate(`/super_admin/investor_portal/investments/opportunities/${savedId}`);
        return lifecycleMapped;
      }

      return mapped;
    } catch (error) {
      const message = error?.data?.message || "Unable to save opportunity.";
      if (submitAction !== "draft") {
        toast.error(message);
      }
      throw error;
    }
  };

  return (
    <div className="pb-4">
      <InvestorPortalDetailHeader
        title={isEdit || localOpportunity ? "Edit" : "Create"}
        highlight="Investment Opportunity"
        onBack={() =>
          opportunityId || localOpportunity?.id
            ? navigate(
                `/super_admin/investor_portal/investments/opportunities/${
                  opportunityId || localOpportunity.id
                }`
              )
            : navigate("/super_admin/investor_portal/investments/opportunities")
        }
      />

      <OpportunityEditor
        mode={isEdit || localOpportunity ? "edit" : "create"}
        initialOpportunity={existing}
        initialStep={typeof resumeStep === "number" ? resumeStep : 0}
        onSave={handleSave}
        onCancel={() =>
          opportunityId || localOpportunity?.id
            ? navigate(
                `/super_admin/investor_portal/investments/opportunities/${
                  opportunityId || localOpportunity.id
                }`
              )
            : navigate("/super_admin/investor_portal/investments/opportunities")
        }
      />
    </div>
  );
}

export default withInvestorPortalGuard(OpportunityFormPage, "investment");
